import { File, Folder } from '@lifeomic/chromicons';
import { FileDeleteAction } from '@lifeomic/phc-web-toolkit/src/components/FileManagementControls/FileDeleteAction';
import { FileDetailsModal } from './FileDetailsModal/FileDetailsModal';
import { FileDownloadAction } from '@lifeomic/phc-web-toolkit/src/components/FileManagementControls/FileDownloadAction';
import { FileRenameAction } from '@lifeomic/phc-web-toolkit/src/components/FileManagementControls/FileRenameAction';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { LoadingContainer } from './FileDetailsModal/LoadingContainer';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { CLIHelp } from '@lifeomic/phc-web-toolkit/src/components/CLIHelp';
import * as React from 'react';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import moment from 'moment';
import queryString from 'query-string';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import clsx from 'clsx';

const fileService = new FileService();

const messages = defineMessages({
  folderContentType: {
    id: 'fileTable.body.folderContentType',
    defaultMessage: 'folder',
  },
  unknown: {
    id: 'fileTable.body.unknownValue',
    defaultMessage: '—',
  },
  headerContentType: {
    id: 'fileTable.header.contentType',
    defaultMessage: 'Content Type',
  },
  headerUploadDate: {
    id: 'fileTable.header.uploadedDate',
    defaultMessage: 'Date Uploaded',
  },
  headerName: {
    id: 'fileTable.header.name',
    defaultMessage: 'Name',
  },
  headerSize: {
    id: 'fileTable.header.size',
    defaultMessage: 'Size',
  },
  noActiveProject: {
    id: 'fileTable.body.noActiveProject',
    defaultMessage: 'No active project.  Select a project to browse files.',
  },
  noFilesFound: {
    id: 'fileTable.body.noFiles',
    defaultMessage: 'No files found',
  },
  loadMore: {
    id: 'fileTable.footer.buttons.loadMore',
    defaultMessage: 'Load More',
  },
  headerCount: {
    id: 'fileTable.header.count',
    defaultMessage: 'Count',
  },
  error: {
    id: 'fileTable.error',
    defaultMessage: 'Error loading file',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  marginRight: {
    marginRight: theme.spacing(1),
  },
  folder: {
    color: theme.palette.blue.main,
  },
  file: {
    color: theme.palette.black[600],
  },
}));

const getColumns = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage,
}) => [
  {
    header: {
      label: formatMessage(messages.headerName),
    },
    cell: {
      content: (file: any) => {
        return (
          <React.Fragment>
            {file.type === 'FOLDER' ? (
              <Folder className={clsx(classes.marginRight, classes.folder)} />
            ) : (
              <File className={clsx(classes.marginRight, classes.file)} />
            )}
            {file.name}
          </React.Fragment>
        );
      },
    },
  },
  {
    header: {
      label: formatMessage(messages.headerContentType),
    },
    cell: {
      content: (file: any) => {
        if (file.type === 'FOLDER') {
          return formatMessage(messages.folderContentType);
        }

        return file.contentType;
      },
    },
  },
  {
    header: {
      label: formatMessage(messages.headerUploadDate),
    },
    cell: {
      content: (file: any) => {
        if (!file.lastModified) {
          return formatMessage(messages.unknown);
        }

        return moment(file.lastModified).format('MM/DD/YY hh:mm A');
      },
    },
  },
  {
    header: {
      label: formatMessage(messages.headerSize),
    },
    cell: {
      content: (file: any) => {
        if (file.type === 'FOLDER' || !file.size) {
          return formatMessage(messages.unknown);
        }

        return (
          <FormattedMessage
            id="fileTable.body.cell.fileSize"
            defaultMessage="{value, number} {unit}"
            values={file.size}
          />
        );
      },
    },
  },
];

const FilesTablev2 = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activePath,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  enableDelete,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  enableEdit,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  enableOpen,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  enableRequestDownloadLink,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  files,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  handleDeleteFile,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  handleLoadMore,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  handleRenameFile,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  hasMoreToLoad,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  isFetching,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onRowClick,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onUpdatePath,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { fileId, account, projectId } = queryString.parse(
    queryString.extract(window.location.href),
  );
  const [isFetchingFile, setIsFetchingFile] = React.useState(Boolean(fileId));
  const [openedFile, setOpenedFile] = React.useState(null);
  const getNewPath = (name: any) =>
    name &&
    name.slice(0, Math.max(name.lastIndexOf('/'), 0)).replace(/^\//, '');

  const [getFile, getFileState] = useService(fileService.getFile);
  const [notify] = useNotify();

  React.useEffect(() => {
    if (fileId) {
      getFile(fileId as string);
    }
  }, [fileId, getFile]);

  React.useEffect(() => {
    if (fileId && getFileState.response && getFileState.response.data) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setOpenedFile(getFileState.response.data);
      const { name } = getFileState.response.data;
      const newPath = getNewPath(name);
      onUpdatePath({ newPath, fileId, account, projectId });
      setIsFetchingFile(false);
    } else if (fileId && getFileState.error) {
      notify(formatMessage(messages.error), 'error');
      setIsFetchingFile(false);
    }
  }, [
    getFileState.response,
    onUpdatePath,
    fileId,
    account,
    projectId,
    notify,
    formatMessage,
    getFileState.error,
  ]);

  const handleRowClick = (file: any) => {
    if (file.type === 'FOLDER') {
      onRowClick && onRowClick(file);
      return;
    }

    setOpenedFile(file);
  };

  const onDismiss = () => {
    setOpenedFile(null);
    if (getFileState.response && getFileState.response.data) {
      const { name } = getFileState.response.data;
      const newPath = getNewPath(name);
      onUpdatePath({ newPath, account, projectId });
    }
  };

  return (
    <React.Fragment>
      {isFetchingFile || Boolean(openedFile) ? (
        <LoadingContainer />
      ) : (
        <InfiniteScroll
          loading={isFetching}
          hasNextPage={hasMoreToLoad}
          loadMore={handleLoadMore}
          scrollOffset={20}
          scrollContainer="parent"
        >
          <TableModule
            isLoading={isFetching}
            config={getColumns({
              classes,
              formatMessage,
            })}
            data={files}
            rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
            onRowClick={handleRowClick}
            rowRole="link"
            rowActions={file => {
              return (
                <>
                  {enableRequestDownloadLink && file.type === 'FILE' && (
                    <FileDownloadAction file={file} />
                  )}
                  {enableEdit && file.type === 'FILE' && (
                    <FileRenameAction
                      file={file}
                      path={activePath}
                      onRename={handleRenameFile}
                    />
                  )}
                  {enableDelete && file.type === 'FILE' && (
                    <FileDeleteAction file={file} onDelete={handleDeleteFile} />
                  )}
                  {file.type === 'FILE' && (
                    <CLIHelp variant="modal" color="inverse">
                      {`lo files download ${file.id}`}
                    </CLIHelp>
                  )}
                </>
              );
            }}
          />
        </InfiniteScroll>
      )}

      {Boolean(openedFile) && (
        <FileDetailsModal
          isOpen={Boolean(openedFile)}
          file={openedFile}
          hasViewAccess={enableOpen}
          onDismiss={onDismiss}
        />
      )}
    </React.Fragment>
  );
};

export default FilesTablev2;
